import { Alert, Button, Descriptions, Drawer, Space, Spin, Tag, Timeline, Typography, message } from 'antd'
import { useEffect, useState } from 'react'

import { downloadContractPdf, errorText, getContract } from '~/services/teacher-contract'
import { ITeacherContractDetail, ITeacherContractEvent } from '~/types/teacher-contract'

import PdfPreviewModal, { PdfPreview } from './PdfPreviewModal'
import { STATUS_COLOR, STATUS_LABEL, fmt } from './constants'

const EVENT_LABEL: Record<ITeacherContractEvent['eventType'], string> = {
  SENT: '发送合同',
  VIEWED: '老师打开合同',
  CODE_SENT: '发送签署验证码',
  CODE_REJECTED: '验证码错误',
  SIGNED: '老师签署',
  VOIDED: '作废',
}

const ContractDetailDrawer = ({ id, onClose }: { id: number | null; onClose: () => void }) => {
  const [detail, setDetail] = useState<ITeacherContractDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [downloading, setDownloading] = useState<'signed' | 'original' | null>(null)
  const [pdf, setPdf] = useState<PdfPreview | null>(null)

  useEffect(() => {
    if (id == null) {
      setDetail(null)
      return
    }
    setLoading(true)
    getContract(id)
      .then((res) => setDetail(res.data))
      .catch(async (err) => message.error(await errorText(err)))
      .finally(() => setLoading(false))
  }, [id])

  const download = async (original: boolean) => {
    if (id == null) return
    setDownloading(original ? 'original' : 'signed')
    try {
      const no = detail?.summary.contractNo ?? 'contract'
      setPdf({
        blob: (await downloadContractPdf(id, original)).data,
        title: `${no} · ${original ? '发出时的原文' : '签署版'}`,
        fileName: original ? `${no}.pdf` : `${no}-signed.pdf`,
      })
    } catch (err) {
      message.error(await errorText(err, '下载失败'))
    } finally {
      setDownloading(null)
    }
  }

  const s = detail?.summary
  return (
    <Drawer open={id != null} onClose={onClose} width={720} title={s ? `${s.contractNo} · ${s.title}` : '合同详情'}>
      <Spin spinning={loading}>
        {detail && s && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Space>
              {s.status === 'SIGNED' && (
                <Button type="primary" loading={downloading === 'signed'} onClick={() => download(false)}>
                  查看签署版 PDF
                </Button>
              )}
              <Button loading={downloading === 'original'} onClick={() => download(true)}>
                查看发出时的原文
              </Button>
            </Space>
            {!detail.auditChainValid && (
              <Alert type="error" showIcon message="审计记录的哈希链对不上：有记录被改动或删除过，请联系开发排查" />
            )}
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="状态">
                <Tag color={STATUS_COLOR[s.status]}>{STATUS_LABEL[s.status]}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="老师">
                {s.teacherName}（{s.teacherEmail}）
              </Descriptions.Item>
              <Descriptions.Item label="报酬">{s.feeSummary || '-'}</Descriptions.Item>
              <Descriptions.Item label="发送时间">{fmt(s.sentAt)}</Descriptions.Item>
              <Descriptions.Item label="老师首次打开">{fmt(s.viewedAt)}</Descriptions.Item>
              <Descriptions.Item label="签署时间">{fmt(s.signedAt)}</Descriptions.Item>
              {s.status === 'SIGNED' && (
                <>
                  <Descriptions.Item label="签署姓名">{detail.signerName}</Descriptions.Item>
                  <Descriptions.Item label="签署邮箱">{detail.signerEmail}</Descriptions.Item>
                  <Descriptions.Item label="签署 IP">{detail.signerIp}</Descriptions.Item>
                </>
              )}
              {s.status === 'VOIDED' && (
                <Descriptions.Item label="作废">
                  {fmt(s.voidedAt)} · {detail.voidReason}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="原文 SHA-256">
                <Typography.Text code copyable style={{ fontSize: 12 }}>
                  {detail.documentSha256}
                </Typography.Text>
              </Descriptions.Item>
              {detail.signedSha256 && (
                <Descriptions.Item label="签署版 SHA-256">
                  <Typography.Text code copyable style={{ fontSize: 12 }}>
                    {detail.signedSha256}
                  </Typography.Text>
                </Descriptions.Item>
              )}
            </Descriptions>
            <div>
              <Typography.Title level={5}>审计记录</Typography.Title>
              <Timeline
                items={detail.events.map((e) => ({
                  color: e.eventType === 'SIGNED' ? 'green' : e.eventType === 'VOIDED' || e.eventType === 'CODE_REJECTED' ? 'red' : 'blue',
                  children: (
                    <div>
                      <div>
                        <b>{EVENT_LABEL[e.eventType]}</b> · {fmt(e.createdAt)} · {e.actorName}
                      </div>
                      <div style={{ color: '#888', fontSize: 12 }}>
                        {[e.ip && `IP ${e.ip}`, e.detail].filter(Boolean).join(' · ')}
                      </div>
                      {e.userAgent && <div style={{ color: '#aaa', fontSize: 11 }}>{e.userAgent}</div>}
                    </div>
                  ),
                }))}
              />
            </div>
          </Space>
        )}
      </Spin>
      <PdfPreviewModal preview={pdf} onClose={() => setPdf(null)} />
    </Drawer>
  )
}

export default ContractDetailDrawer
